import React, { useCallback, useEffect, useState } from 'react';

/**
 * Settings > AI — per-account provider credentials (OpenAI / OpenRouter),
 * model defaults, token caps and the usage report. Admin-only (the API
 * enforces it; non-admins see the error state).
 * Task: eden-api-mcp-ai-2026-07-10.
 */

function csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';
}

async function api(method, url, body) {
    const response = await fetch(url, {
        method,
        credentials: 'same-origin',
        headers: {
            Accept: 'application/json',
            'X-CSRF-TOKEN': csrfToken(),
            ...(body ? { 'Content-Type': 'application/json' } : {}),
        },
        body: body ? JSON.stringify(body) : undefined,
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(payload?.error?.message ?? `Request failed (${response.status})`);
    }
    return payload;
}

const PROVIDER_LABELS = {
    openai: 'OpenAI',
    openrouter: 'OpenRouter',
};

function CredentialCard({ provider, credential, onSaved, onError }) {
    const [apiKey, setApiKey] = useState('');
    const [defaultModel, setDefaultModel] = useState(credential?.default_model ?? '');
    const [monthlyCap, setMonthlyCap] = useState(credential?.monthly_token_cap ?? '');
    const [perUserCap, setPerUserCap] = useState(credential?.per_user_token_cap ?? '');
    const [enabled, setEnabled] = useState(credential?.enabled ?? true);
    const [saving, setSaving] = useState(false);
    const [testResult, setTestResult] = useState(null);

    useEffect(() => {
        setDefaultModel(credential?.default_model ?? '');
        setMonthlyCap(credential?.monthly_token_cap ?? '');
        setPerUserCap(credential?.per_user_token_cap ?? '');
        setEnabled(credential?.enabled ?? true);
    }, [credential]);

    const save = async (event) => {
        event.preventDefault();
        setSaving(true);
        setTestResult(null);
        try {
            await api('POST', '/api/v1/ai/credentials', {
                provider,
                ...(apiKey ? { api_key: apiKey } : {}),
                default_model: defaultModel || null,
                monthly_token_cap: monthlyCap === '' ? null : Number(monthlyCap),
                per_user_token_cap: perUserCap === '' ? null : Number(perUserCap),
                enabled,
            });
            setApiKey('');
            await onSaved();
        } catch (e) {
            onError(e.message);
        } finally {
            setSaving(false);
        }
    };

    const test = async () => {
        setTestResult(null);
        try {
            const payload = await api('POST', '/api/v1/ai/credentials/test', { provider });
            setTestResult(payload.data.ok
                ? { ok: true, message: 'Connection OK — the key works.' }
                : { ok: false, message: payload.data.message ?? 'The provider rejected the key.' });
        } catch (e) {
            setTestResult({ ok: false, message: e.message });
        }
    };

    const remove = async () => {
        if (!window.confirm(`Remove the ${PROVIDER_LABELS[provider]} key? Chat stops working if no other provider is configured.`)) {
            return;
        }
        try {
            await api('DELETE', `/api/v1/ai/credentials/${provider}`);
            await onSaved();
        } catch (e) {
            onError(e.message);
        }
    };

    return (
        <div className="panel panel-default with-rounded-corner">
            <div className="panel-heading">
                <h4 style={{ display: 'inline-block', marginRight: 10 }}>{PROVIDER_LABELS[provider]}</h4>
                {credential
                    ? <span className={`label ${credential.enabled ? 'label-success' : 'label-default'}`}>
                        {credential.enabled ? 'configured' : 'disabled'} · key {credential.key_hint}
                    </span>
                    : <span className="label label-default">not configured</span>}
            </div>
            <div className="panel-body">
                <form onSubmit={save} className="form-horizontal">
                    <div className="form-group">
                        <label className="col-sm-3 control-label">API key</label>
                        <div className="col-sm-9">
                            <input
                                type="password"
                                className="form-control"
                                value={apiKey}
                                onChange={(e) => setApiKey(e.target.value)}
                                placeholder={credential ? '(unchanged — enter a new key to rotate)' : 'Paste the provider API key'}
                                autoComplete="new-password"
                            />
                        </div>
                    </div>
                    <div className="form-group">
                        <label className="col-sm-3 control-label">Default model</label>
                        <div className="col-sm-9">
                            <input
                                className="form-control"
                                value={defaultModel}
                                onChange={(e) => setDefaultModel(e.target.value)}
                                placeholder={provider === 'openrouter' ? 'e.g. anthropic/claude-sonnet-4.5' : 'e.g. gpt-5-mini'}
                            />
                        </div>
                    </div>
                    <div className="form-group">
                        <label className="col-sm-3 control-label">Monthly token cap</label>
                        <div className="col-sm-3">
                            <input type="number" min="0" className="form-control" value={monthlyCap}
                                onChange={(e) => setMonthlyCap(e.target.value)} placeholder="unlimited" />
                        </div>
                        <label className="col-sm-3 control-label">Per-user cap</label>
                        <div className="col-sm-3">
                            <input type="number" min="0" className="form-control" value={perUserCap}
                                onChange={(e) => setPerUserCap(e.target.value)} placeholder="unlimited" />
                        </div>
                    </div>
                    <div className="form-group">
                        <div className="col-sm-9 col-sm-offset-3">
                            <label className="checkbox-inline">
                                <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> Enabled
                            </label>
                        </div>
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                        <div className="col-sm-9 col-sm-offset-3">
                            <button type="submit" className="btn btn-primary" disabled={saving}>
                                {saving ? 'Saving…' : 'Save'}
                            </button>{' '}
                            {credential && (
                                <>
                                    <button type="button" className="btn btn-default" onClick={test}>Test connection</button>{' '}
                                    <button type="button" className="btn btn-danger" onClick={remove}>Remove</button>
                                </>
                            )}
                            {testResult && (
                                <span className={testResult.ok ? 'text-success' : 'text-danger'} style={{ marginLeft: 10 }}>
                                    {testResult.message}
                                </span>
                            )}
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function AiSettings() {
    const [credentials, setCredentials] = useState([]);
    const [usage, setUsage] = useState(null);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setError(null);
        try {
            const [credentialsPayload, usagePayload] = await Promise.all([
                api('GET', '/api/v1/ai/credentials'),
                api('GET', '/api/v1/ai/usage'),
            ]);
            setCredentials(credentialsPayload.data ?? []);
            setUsage(usagePayload);
        } catch (e) {
            setError(e.message);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const credentialFor = (provider) => credentials.find((credential) => credential.provider === provider) ?? null;

    return (
        <div className="ai-settings">
            {error && <div className="alert alert-danger">{error}</div>}

            {['openrouter', 'openai'].map((provider) => (
                <CredentialCard
                    key={provider}
                    provider={provider}
                    credential={credentialFor(provider)}
                    onSaved={load}
                    onError={setError}
                />
            ))}

            <div className="panel panel-default with-rounded-corner">
                <div className="panel-heading"><h4>Usage this period {usage?.meta ? `(${usage.meta.from} → ${usage.meta.to})` : ''}</h4></div>
                <div className="panel-body">
                    {!usage && <p>Loading…</p>}
                    {usage && (
                        <>
                            <p>
                                <strong>{Number(usage.data.totals.calls).toLocaleString()}</strong> calls ·{' '}
                                <strong>{Number(usage.data.totals.tokens_in).toLocaleString()}</strong> tokens in ·{' '}
                                <strong>{Number(usage.data.totals.tokens_out).toLocaleString()}</strong> tokens out
                            </p>
                            <div className="row">
                                <div className="col-sm-6">
                                    <h5>By model</h5>
                                    <table className="table table-condensed">
                                        <thead><tr><th>Provider</th><th>Model</th><th>Calls</th><th>Tokens</th></tr></thead>
                                        <tbody>
                                            {usage.data.by_model.map((row, index) => (
                                                <tr key={index}>
                                                    <td>{row.provider}</td>
                                                    <td>{row.model}</td>
                                                    <td>{row.calls}</td>
                                                    <td>{(Number(row.tokens_in) + Number(row.tokens_out)).toLocaleString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="col-sm-6">
                                    <h5>By user</h5>
                                    <table className="table table-condensed">
                                        <thead><tr><th>User id</th><th>Calls</th><th>Tokens</th></tr></thead>
                                        <tbody>
                                            {usage.data.by_user.map((row, index) => (
                                                <tr key={index}>
                                                    <td>{row.user_id ?? '—'}</td>
                                                    <td>{row.calls}</td>
                                                    <td>{(Number(row.tokens_in) + Number(row.tokens_out)).toLocaleString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
